import { useEffect, useState } from "react";
import {
  accountScope,
  computeFormBadge,
  computeHomeWeekly,
  DEFAULT_UNITS_PREFERENCE,
  recentPrs,
  SessionResult,
  countsAsStrength,
  hardSetsOfResult,
  startedAtMsOf,
  type FormBadge,
  type FormSession,
  type HomeWeekly,
  type PRsByExercise,
  type RecentPr,
  type UnitsPreference,
} from "@heraion/shared";

import { apiFetch } from "./api";
import { lsCache } from "./lsCache";
import { bodyweightOf, unitOfDoc, useShellProfile } from "./ThemeProfileBridge";

type CachedSignals = {
  formBadge: FormBadge | null;
  weekly: HomeWeekly | null;
  prs: RecentPr[];
  units: UnitsPreference;
  bodyweightKg: number | null;
};

// localStorage SWR cache (see the hook comment). Versioned key so a shape
// change invalidates cleanly; best-effort (private mode / quota → no-op).
// Through lsCache (#397): a copy of the account's rows, dropped with it.
const signalsCache = lsCache<CachedSignals>("jl.home.signals.v3");

// Lean Home-data fetch for the web-rnw Plan surface: the readiness badge (from
// the same /api/session-results page Progress reads), the recent-PRs strip
// (/api/me/records), and the profile (units + current bodyweight, for the
// PRs strip + quick weigh-in). Mobile has its own cached hooks; web only needs
// a one-shot read, so this stays local. The derivations are the shared ones,
// so the numbers match mobile. refreshProfile re-reads after a weigh-in.
// The profile is the shell's one doc (#306): the cache paints units and
// bodyweight only until it lands, for display. A write takes its unit from
// useProfileUnitOrNull (#343), never from here.
export function useHomeSignals(): {
  formBadge: FormBadge | null;
  weekly: HomeWeekly | null;
  prs: RecentPr[];
  units: UnitsPreference;
  bodyweightKg: number | null;
  refreshProfile: () => Promise<void>;
} {
  // Stale-while-revalidate cache (mobile reads records cache-first too;
  // /api/me/records recomputes PRs from full history, so a cold fetch is
  // ~seconds). Paint the last good signals from localStorage immediately,
  // then refresh underneath — so a page reload (the test loop) is instant.
  const [cache] = useState(signalsCache.peek);
  const [formBadge, setFormBadge] = useState<FormBadge | null>(
    cache?.formBadge ?? null,
  );
  const [weekly, setWeekly] = useState<HomeWeekly | null>(
    cache?.weekly ?? null,
  );
  const [prs, setPrs] = useState<RecentPr[]>(cache?.prs ?? []);
  const { doc, refresh: refreshProfile } = useShellProfile();
  const units: UnitsPreference =
    unitOfDoc(doc) ?? cache?.units ?? DEFAULT_UNITS_PREFERENCE;
  const bodyweightKg = doc === null ? (cache?.bodyweightKg ?? null) : bodyweightOf(doc);
  // Raw sessions, kept apart from `weekly` (below), which is derived from them.
  const [sessions, setSessions] = useState<FormSession[] | null>(null);

  // #398: the signals are this mount's, so the account they are for is the
  // one of the mount: past an account change they are no longer written.
  // Sign-out unmounts it anyway (AuthContext signOut: drop, then leave; App.tsx).
  const [scope] = useState(accountScope);
  // Persist the latest signals so the next mount paints instantly.
  useEffect(() => {
    void signalsCache.write({ formBadge, weekly, prs, units, bodyweightKg }, scope);
  }, [formBadge, weekly, prs, units, bodyweightKg, scope]);

  useEffect(() => {
    let cancelled = false;
    // The two reads are independent — run them in parallel so the PRs strip
    // (records) doesn't wait on the heavier session-results page.
    const loadBadge = async () => {
      try {
        const resp = await apiFetch<{ items?: unknown[] }>(
          "/api/session-results?limit=200",
        );
        const loaded: FormSession[] = [];
        for (const raw of resp.items ?? []) {
          const parsed = SessionResult.safeParse(raw);
          if (!parsed.success) continue;
          // #419: a walk is not a training day.
          if (!countsAsStrength(parsed.data)) continue;
          loaded.push({
            startedAtMs: startedAtMsOf(raw, parsed.data),
            volumeKg: parsed.data.totalVolumeKg,
            // §8ter/`HM-3` — l'asse dello stato è la SERIE DURA, non il
            // tonnellaggio. ⛔ Era già in `parsed.data` e veniva scartato qui.
            hardSets: hardSetsOfResult(parsed.data),
          });
        }
        if (!cancelled) {
          setFormBadge(computeFormBadge(loaded, Date.now()));
          setSessions(loaded);
        }
      } catch {
        // leave the badge + weekly null — the cards hide themselves
      }
    };
    const loadPrs = async () => {
      try {
        const rec = await apiFetch<{ records?: PRsByExercise }>(
          "/api/me/records",
        );
        if (!cancelled) setPrs(recentPrs(rec.records ?? {}, Date.now()));
      } catch {
        // leave prs empty — the strip hides itself
      }
    };
    void loadBadge();
    void loadPrs();
    return () => {
      cancelled = true;
    };
  }, []);

  // The last 7 days (#463), recomputed when the sessions fetch lands.
  useEffect(() => {
    if (sessions === null) {
      setWeekly(null);
      return;
    }
    setWeekly(computeHomeWeekly(sessions, Date.now()));
  }, [sessions]);

  return { formBadge, weekly, prs, units, bodyweightKg, refreshProfile };
}
